import { Box, Button, Dialog, DialogActions, DialogTitle, DialogContent, IconButton, DialogProps } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import React, { useMemo, useState } from 'react';
import { styles } from './RenameFolderDialog.styles';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { FolderTitleInput } from '../FolderTitleInput/FolderTitleInput';
import { Folder } from '../../types';
import { getTextFieldIconByStatus } from '@opengov/capital-mui-theme';
import { findFolderByParentId } from '../LocationSelect/hooks/useParentFolder';

const intl = {
  title: 'Rename Folder',
  cancel: 'Cancel',
  rename: 'Rename',
  closeIconAriaLabel: 'close button for dialog',
  uniqueNameValidationError: 'Provide a unique name for your folder'
};

type RenameFolderDialogProps = DialogProps & {
  folderToRename: Folder;
  isRenaming?: boolean;
  title?: string;
  onClose: () => void;
  onRename: (folder: Folder) => Promise<void>;
};

export function RenameFolderDialog({
  folderToRename,
  isRenaming = false,
  title = intl.title,
  onRename,
  ...dialogProps
}: RenameFolderDialogProps) {
  const { rootFolder } = useFoldersContext();
  const parentFolder = useMemo(() => {
    const rootFolderSubfolders = rootFolder.subfolders || [];
    return findFolderByParentId(rootFolderSubfolders, folderToRename.parentFolderId) || rootFolder;
  }, [rootFolder.subfolders, folderToRename.parentFolderId]);
  const subfolders = parentFolder.subfolders || [];

  const [error, setError] = useState<string | null>(null);
  const [folderTitle, setFolderTitle] = useState(folderToRename.title);
  const trimmedFolderTitle = useMemo(() => folderTitle.trim(), [folderTitle]);
  const isFolderTitleUnique = useMemo(() => {
    return !subfolders.find((folder) => folder.title === trimmedFolderTitle && folder.id !== folderToRename.id);
  }, [trimmedFolderTitle, subfolders, folderToRename]);
  const isRenameDisabled = useMemo(
    () =>
      isRenaming ||
      trimmedFolderTitle.length === 0 ||
      !isFolderTitleUnique ||
      folderToRename.title === trimmedFolderTitle,
    [trimmedFolderTitle, isFolderTitleUnique, isRenaming, folderToRename]
  );
  const helperTextWithIcon = useMemo(() => {
    const helperText = !isFolderTitleUnique ? intl.uniqueNameValidationError : error;
    return helperText ? [getTextFieldIconByStatus('error') as JSX.Element, helperText] : undefined;
  }, [isFolderTitleUnique, error]);

  const handleFolderTitleChange = (value: string) => {
    setError(null);
    setFolderTitle(value);
  };

  const handleRename = async () => {
    setError(null);
    try {
      await onRename({ ...folderToRename, title: trimmedFolderTitle });
      dialogProps.onClose();
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
    }
  };

  return (
    <Dialog fullWidth {...dialogProps}>
      <Box sx={styles.header}>
        <DialogTitle>{title}</DialogTitle>
        <IconButton aria-label={intl.closeIconAriaLabel} onClick={dialogProps.onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        <FolderTitleInput
          withLabel={true}
          disabled={isRenaming}
          title={folderTitle}
          error={!isFolderTitleUnique || Boolean(error)}
          helperText={helperTextWithIcon}
          onChange={handleFolderTitleChange}
          onPressEnter={() => {
            if (!isRenameDisabled) {
              void handleRename();
            }
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={isRenaming} color="secondary" data-test="cancelRenameButton" onClick={dialogProps.onClose}>
          {intl.cancel}
        </Button>
        <LoadingButton
          loading={isRenaming}
          disabled={isRenameDisabled}
          color="primary"
          variant="contained"
          data-test="renameFolderButton"
          onClick={() => {
            void handleRename();
          }}
        >
          {intl.rename}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
